import React from 'react';
import { Link } from 'react-router-dom';
import { parseEm } from '../inlineEm';
import { useMarketingCourses } from '../MarketingData';
import { LabSequence } from './LabSequence';
import { ReviseBundleWindow } from './BrowseWindow';
import { ReviseTryLesson } from './TryLesson';
import { RevisePricing } from './Pricing';
import { ShotFrame } from './Shot';
import { CtaLink } from './links';
import { IEB_LIVE, IEB_PAGE, LABS } from './content';
import { findIebCourse, isIebStudyLab } from './iebLabs';
import { PublicCourseCard } from '../../PublicCourseCard';
import { uniformBadgeFlags } from '../../../lib/cardBadges';
import type { PublicCourse } from '../../../services/publicService';

/**
 * `/ieb` — the IEB range, on its own page.
 *
 * ## Why a page and not a section
 *
 * A learner writes exactly one board. The landing page is the NSC's — the
 * free papers are its funnel and every screenshot on it is NSC material — so
 * the IEB candidate gets one quiet line under the hero and this page behind
 * it, rather than a section they must scroll NSC arguments to reach, or a
 * landing page that hedges every sentence for two audiences at once.
 *
 * ## What it reuses
 *
 * The /free page's whole vocabulary: `.rv-hero` and `.rv-start` for the navy
 * bands, `.rv-sec` / `.rv-head` between, and `LabSequence` for the essay
 * walkthrough — the same four panels the landing page renders, because the
 * Essay Lab reads an IEB essay exactly the way it reads an NSC one.
 *
 * The range is a CURATED topic map (IEB_PAGE.map), not a live course list —
 * see iebLabs.ts for why. What stays live is the linking: a topic becomes a
 * link the day its course is published, and is plain text until then.
 *
 * ## The page is revise-only
 *
 * `pages/IebPage.tsx` turns any other theme away, exactly as /free does.
 */

const Topic: React.FC<{
  topic: { title: string; match: string };
  courses: PublicCourse[];
}> = ({ topic, courses }) => {
  /* Launch gate: while IEB_LIVE is false no chip links anywhere, published
     or not — the page is a teaser and its doors are shut. */
  const course = IEB_LIVE ? findIebCourse(topic.match, courses) : null;
  if (!course) return <span className="rv-map-chip">{topic.title}</span>;
  return (
    <Link className="rv-map-chip rv-map-chip-live" to={`/course/${course.id}`}>
      {topic.title} <span aria-hidden="true">&rarr;</span>
    </Link>
  );
};

export const ReviseIebPage: React.FC = () => {
  const { courses } = useMarketingCourses();
  const map = IEB_PAGE.map;

  /* The shelf under the map: the first few published IEB STUDY Labs as the
     catalogue's own cards — Study Labs only, because the shelf is evidence
     for the topic map directly above it (see isIebStudyLab). Title order,
     which is stable — the catalogue's own `total_students` order would
     reshuffle the shelf as students enrolled. */
  const iebCourses = courses
    .filter((c) => isIebStudyLab(c.title))
    .sort((a, b) => a.title.localeCompare(b.title));
  const shelf = iebCourses.slice(0, map.shelfCap);
  const shelfRest = iebCourses.length - shelf.length;
  /* Badge uniformity judged across the IEB shelf itself, the same rule the
     catalogue grids apply to theirs. */
  const { hideCategory, hideLevel } = uniformBadgeFlags(iebCourses);

  return (
    <>
      {/* `data-accent="study"` end to end: the Study Labs are this page's core
          offer, the way the Practice Labs are /free's — that page carries
          their exam blue for the same reason this one carries gold. */}
      <section className="rv-hero rv-fhero" data-accent="study">
        <div className="rv-wrap">
          <p className="rv-kicker">{IEB_PAGE.hero.kicker}</p>
          <h1>{parseEm(IEB_PAGE.hero.headline)}</h1>
          <p className="rv-lede">{IEB_PAGE.hero.lede}</p>
          <div className="rv-ctas">
            {IEB_LIVE ? (
              <CtaLink className="rv-btn rv-btn-primary" href={IEB_PAGE.cta.href}>
                {IEB_PAGE.hero.ctaLabel}
              </CtaLink>
            ) : (
              <p className="rv-launchpill">{IEB_PAGE.launch.pill}</p>
            )}
            <p className="rv-reassure">
              <b>{IEB_LIVE ? IEB_PAGE.hero.reassure : IEB_PAGE.launch.reassure}</b>
            </p>
          </div>
        </div>
      </section>

      <section className="rv-sec" id="labs" data-accent="study">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{map.kicker}</p>
            <h2>{parseEm(map.heading)}</h2>
            <p className="rv-lede">{map.lede}</p>
          </div>

          {/* The demo comes before the range, as it does on the landing page:
              the reader should meet a lab before being shown fourteen of
              them. Same component, same lesson — see IEB_PAGE.demo for why
              that content is right for this board too. */}
          <div className="rv-demo-lead rv-band-centre">
            <p className="rv-p">{IEB_PAGE.demo.body}</p>
            <ReviseTryLesson id="try" tag={IEB_PAGE.demo.tag} />
          </div>

          <div className="rv-map">
            {map.groups.map((group) => (
              <div className="rv-map-group" key={group.label}>
                <p className="rv-map-label">
                  {group.label}
                  <span className="rv-map-count">{group.note}</span>
                </p>
                <div className="rv-map-chips">
                  {group.topics.map((topic) => (
                    <Topic key={topic.title} topic={topic} courses={courses} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* The shelf sits directly under the topic groups it proves — the
              cards ARE Study Labs, so tiles after the Skills Labs block read
              as evidence for the wrong claim. Rendered only once something is
              published: before launch the map closes into the skills panel on
              its own, and an empty white panel would turn "coming" into
              "broken". */}
          {shelf.length > 0 && (
            /* `data-gated` while the launch gate holds: the tiles stay — the
               whole point of the teaser — but nothing on them is clickable. */
            <div
              className="rv-browse-window rv-map-shelf"
              data-cards="courses"
              data-gated={IEB_LIVE ? undefined : 'true'}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {shelf.map((course) => (
                  <PublicCourseCard
                    key={course.id}
                    course={course}
                    hideCategory={hideCategory}
                    hideLevel={hideLevel}
                  />
                ))}
              </div>
              {shelfRest > 0 && <p className="rv-map-shelf-more">{map.shelfMore(shelfRest)}</p>}
            </div>
          )}

          <div className="rv-ctas rv-sec-cta">
            {IEB_LIVE ? (
              <CtaLink className="rv-btn rv-btn-primary" href={map.cta.href}>
                {map.cta.label}
              </CtaLink>
            ) : (
              <p className="rv-launchpill">{IEB_PAGE.launch.pill}</p>
            )}
          </div>
        </div>
      </section>

      {/* Skills Labs, a full section of their own (Dylan): the lab-section
          head Essay Labs uses — small product kicker, big statement — the
          landing's free label at the landing's weight, and the chips on a
          brand-pink washed panel. The essay accent IS the brand pink, so the
          wash derives from the section accent like every other surface. */}
      <section className="rv-sec" id="skills" data-accent="essay">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{IEB_PAGE.skills.kicker}</p>
            <h2>{parseEm(IEB_PAGE.skills.heading)}</h2>
            <p className="rv-lede">{IEB_PAGE.skills.lede}</p>
          </div>
          <p className="rv-kicker rv-freelabel">{IEB_PAGE.skills.freeLabel}</p>
          <div className="rv-map-skills">
            <div className="rv-map-chips">
              {IEB_PAGE.skills.topics.map((topic) => (
                <Topic key={topic.title} topic={topic} courses={courses} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="rv-sec" id="essay" data-accent="essay">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{IEB_PAGE.essay.kicker}</p>
            <h2>{parseEm(IEB_PAGE.essay.heading)}</h2>
            <p className="rv-lede">{IEB_PAGE.essay.lede}</p>
          </div>
          {/* The landing page's own handwriting capture, from LABS.essay so
              the picture and its caption stay single-sourced — the Essay Lab
              reads an IEB essay exactly the way it reads an NSC one, and both
              pages should show the same proof of it. */}
          {LABS.essay.solo && (
            <div className="rv-figure rv-solo">
              <ShotFrame shot={LABS.essay.solo.shot} />
              {LABS.essay.solo.caption && <p className="rv-cap">{LABS.essay.solo.caption}</p>}
            </div>
          )}
          <LabSequence lab="essay" />
        </div>
      </section>

      {/* The IEB's own prices, not the NSC's — see IEB_PAGE.pricing. It sits
          after the three products have been argued for and before the ask,
          which is where the landing page puts its own. */}
      <RevisePricing pricing={IEB_PAGE.pricing} anchor="pricing" />

      {/* The three sets as real, clickable cards under the pricing that
          explains them. Behind the launch gate with everything else: a teaser
          page must not carry a buy shelf. */}
      {IEB_LIVE && <ReviseBundleWindow board="ieb" />}

      {/* `rv-sec` as well as `rv-start`, exactly as /free's closing band —
          `.rv-start` styles the navy canvas only, the padding lives on
          `.rv-sec`. Left-aligned like the rest of this page. */}
      <section className="rv-sec rv-start" data-accent="study">
        <div className="rv-wrap">
          <div className="rv-head">
            <p className="rv-kicker">{IEB_PAGE.cta.kicker}</p>
            <h2>{parseEm(IEB_PAGE.cta.headline)}</h2>
          </div>
          <div className="rv-ctas">
            {IEB_LIVE ? (
              <CtaLink className="rv-btn rv-btn-primary" href={IEB_PAGE.cta.href}>
                {IEB_PAGE.cta.buttonLabel}
              </CtaLink>
            ) : (
              <p className="rv-launchpill">{IEB_PAGE.launch.pill}</p>
            )}
          </div>
        </div>
      </section>
    </>
  );
};
